import React from 'react';
import { motion } from 'framer-motion';
import { SiteShell } from '../components/landing/new/SiteShell';
import { MarketingFooter } from '../components/landing/new/MarketingFooter';
import { ContractorSection } from '../components/solutions/ContractorSection';
import { InsurerSection } from '../components/solutions/InsurerSection';
import { OwnerSection } from '../components/solutions/OwnerSection';
import { Zap, Lock, BarChart3, Database } from 'lucide-react';

const SolutionsPage = () => {
  return (
    <SiteShell>
      {/* Hero Section */}
      <section className="py-20 md:py-32 relative">
        <div className="text-center max-w-3xl mx-auto px-6 mb-20">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-7xl font-bold tracking-tight text-white mb-6"
          >
            Built for the entire <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-200">
              risk lifecycle.
            </span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-slate-300 leading-relaxed font-light"
          >
            From the field worker's pocket to the underwriter's desk, Virtual Hardhat unifies safety operations and risk transfer.
          </motion.p>
        </div>
      </section>

      {/* Deep Dive Sections */}
      <ContractorSection />
      <InsurerSection />
      <OwnerSection />

      {/* Data Engine Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="w-full max-w-[1200px] mx-auto px-6">
            <div className="bg-[#0F141F] rounded-[40px] border border-white/10 p-8 md:p-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-grid-white/[0.02] bg-[length:32px_32px]" />
                <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                    <div>
                        <div className="flex items-center gap-2 text-purple-400 font-mono text-xs uppercase tracking-widest mb-4">
                            <Database size={14} />
                            <span>The Core Technology</span>
                        </div>
                        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                            The Data Engine
                        </h2>
                        <p className="text-slate-300 text-lg mb-8 leading-relaxed">
                            Under the hood, our RAG (Retrieval-Augmented Generation) engine contextualizes every data point. A photo isn't just a photo—it's evidence linked to a permit, a worker, a location, and a policy.
                        </p>
                        <div className="space-y-6">
                            {[
                                { icon: Zap, title: 'Real-time Ingestion', desc: 'IoT sensors and mobile inputs processed in <100ms.' },
                                { icon: Lock, title: 'Cryptographic Chain', desc: 'Immutable audit logs for every action.' },
                                { icon: BarChart3, title: 'Predictive Models', desc: 'Forecast risk events before they occur.' }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center shrink-0 border border-white/10">
                                        <item.icon className="text-white" size={24} />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-bold text-lg">{item.title}</h4>
                                        <p className="text-slate-400">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="relative h-[500px] bg-black/40 rounded-3xl border border-white/10 overflow-hidden backdrop-blur-sm shadow-2xl">
                        {/* Abstract Data Viz */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="relative w-64 h-64">
                                <div className="absolute inset-0 border-2 border-orange-500/30 rounded-full animate-[spin_10s_linear_infinite]" />
                                <div className="absolute inset-4 border-2 border-blue-500/30 rounded-full animate-[spin_15s_linear_infinite_reverse]" />
                                <div className="absolute inset-8 border-2 border-emerald-500/30 rounded-full animate-[spin_20s_linear_infinite]" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-32 h-32 bg-slate-800 rounded-full flex items-center justify-center border border-white/10 shadow-2xl">
                                        <div className="text-center">
                                            <div className="text-4xl font-bold text-white">2.4M</div>
                                            <div className="text-xs text-slate-500 uppercase tracking-widest">Events/Day</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
      </section>

      <MarketingFooter />
    </SiteShell>
  );
};

export default SolutionsPage;
